// @ts-strict-ignore
import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  ButtonVariant,
  Modal,
  MultiSelect,
  Table,
  useSnackBar,
} from '@usealloy/component-library';
import { useFetchedUserContext } from '~/helpers/hooks';
import { typhonRoutes } from '~/helpers/routes';
import { typhonAjaxFetch } from '~/helpers/api';
import {
  ACCOUNT_STATUS_OPTIONS,
  ACCOUNT_TABLE_COLUMNS, AccountStatusOption, AccountStatusSingleOption,
} from './constants';
import type { AccountStatusUpdateModalProps, TypeOption } from './types';

const AccountStatusUpdateModal: React.FC<AccountStatusUpdateModalProps> = ({
  isOpen,
  closeModal,
  bankAccount,
  investigationToken,
}) => {
  const [selectedAccountStatus, setSelectedAccountStatus] = useState<TypeOption | null>(null);
  const userContext = useFetchedUserContext();
  const addSnackBar = useSnackBar();

  const handleCloseModal = () => {
    setSelectedAccountStatus(null);
    closeModal();
  };

  const handleAccountStatusUpdate = async () => {
    const url = `${userContext.typhonDomain}${typhonRoutes.bankAccounts.patch.status}`;
    try {
      await typhonAjaxFetch(url, {
        method: 'PATCH',
        body: JSON.stringify({
          bank_account_tokens: [bankAccount?.bank_account?.bank_account_token],
          new_status: selectedAccountStatus?.value?.toLowerCase(),
          investigation_token: investigationToken,
        }),
      });

      addSnackBar({
        type: 'success',
        body: 'Success! Webhook sent',
        title: '',
      });
      handleCloseModal();
    } catch (error) {
      addSnackBar({
        type: 'danger',
        body: 'An unexpected error occurred sending the webhook, please try again.',
        title: '',
      });
    }
  };


  const filteredAccountStatusOptions = useMemo(() => ACCOUNT_STATUS_OPTIONS.filter(
    option => (
      option.value.toLowerCase() !== bankAccount?.bank_account?.account_type_status?.toLowerCase()
    ),
  ),
  [bankAccount]);

  return (
    <Modal
      data-testid="account-status-update-modal"
      header="Change Account Status"
      isOpen={isOpen}
      onClose={handleCloseModal}
      primaryAction={
        (
          <Button
            onClick={handleAccountStatusUpdate}
            variant={ButtonVariant.primary}
            disabled={
              !selectedAccountStatus
              || (
                selectedAccountStatus?.value.toLowerCase()
                === bankAccount?.bank_account?.account_type_status?.toLowerCase()
              )
            }
          >
            Submit
          </Button>
        )
      }
      secondaryAction={
        (
          <Button
            onClick={handleCloseModal}
            variant={ButtonVariant.secondary}
          >
            Cancel
          </Button>
        )
      }
    >
      <Box gap={4} direction="column" style={{ padding: '24px 12px' }}>
        <Table columns={ACCOUNT_TABLE_COLUMNS} data={[bankAccount]} />
      </Box>
      <Box gap={4} direction="row" style={{ padding: '0px 12px' }}>
        <MultiSelect
          label="Status"
          options={filteredAccountStatusOptions}
          placeholder="Select Status"
          onChange={(option: TypeOption) => {
            setSelectedAccountStatus(option);
          }}
          selectedOptions={selectedAccountStatus}
          visibleSelectedOptions
          isMulti={false}
          required
          components={{
            Option: AccountStatusOption,
            SingleValue: AccountStatusSingleOption,
          }}
        />
      </Box>
    </Modal>
  );
};

export default AccountStatusUpdateModal;
